// @ts-check
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';

import FilterCombiner from '../../enums/FilterCombiner';

import BusComponent from '../BusComponent';
import TypeDropdown from '../dataFilter/TypeDropdown';
import Filter from './Filter';
import NewCriterion from './NewCriterion';
import AppConfig from '../../appConfig';
import classNames from 'classnames';

/**
 * @typedef State
 * @property {import('../../').PointsFilterField[]} filters
 * @property {import('../../').AppliedPointFilters} appliedFilters
 * @property {import('../../').AppliedPointFilters} pendingAppliedFilters
 * @property {import('../../').PointsFilterField[]} addedCriteria
 * @property {string} filterCombinerType
 * @property {string} lastAppliedFilterCombinerType
 * @property {boolean} hidden
 * @property {() => void} onClose
 *
 * @typedef Props
 * @property {import('react-intl').intlShape} intl
 * @property {boolean} hidden
 *
 * @extends {BusComponent<Props, State>}
 */
class FilterPanel extends BusComponent {
    constructor(props, context) {
        super(props, context);
        /** @type {State} */
        this.state = {
            filters: [],
            appliedFilters: {},
            filterCombinerType: FilterCombiner.MATCH_ALL.type,
            lastAppliedFilterCombinerType: FilterCombiner.MATCH_ALL.type,
            addedCriteria: [],
            pendingAppliedFilters: {},
        };
    }

    componentDidMount() {
        this.bindGluBusEvents({
            POINTS_FILTERS_RESPONSE: this.onPointsFiltersResponse,
            CLEAR_FACILITY_FILTER_FROM_PANEL: this.clearFilters,
            APPLIED_FILTERS_RESPONSE: this.onApplyFiltersResponse,
        });
        this.emit('POINTS_FILTERS_REQUEST');
    }

    onApplyFiltersResponse = eventMap => {
        if (eventMap.appliedGeoFilters) {
            this.setState({
                appliedFilters: eventMap.appliedGeoFilters.appliedFilters,
            });
            this.emit('MAP_APPLY_GEO_JSON_LAYER_UPDATE', {
                filterCombinerType: eventMap.appliedGeoFilters.filterCombinerType,
                appliedFilters: eventMap.appliedGeoFilters.appliedFilters,
            });
        }
    };

    componentWillUnmount() {
        this.unbindGluBusEvents();
    }

    /**
     *
     * @param {import('../../').PointsFilterField[]} filters
     * @param {import('../../').PointsFilterField[]} receivedAddedCriteria
     * @param {import('../../').AppliedPointFilters} receivedAppliedFilters
     * @param {string} receivedFilterCombinerType
     */
    onPointsFiltersResponse = (
        filters,
        receivedAddedCriteria,
        receivedAppliedFilters,
        receivedFilterCombinerType,
    ) => {
        this.setState({
            filters,
            addedCriteria: receivedAddedCriteria || [],
            appliedFilters: receivedAppliedFilters || {},
            filterCombinerType: receivedFilterCombinerType || FilterCombiner.MATCH_ALL.type,
            lastAppliedFilterCombinerType:
                receivedFilterCombinerType || FilterCombiner.MATCH_ALL.type,
            pendingAppliedFilters: {},
        });
    };

    /**
     *
     * @param {import('../../').PointsFilterField} filter
     * @param {string | number} value
     * @param {import('../../').FilterType} filterType
     */
    addFilter = (filter, value, filterType) => {
        let criteria = [];
        if (this.state.addedCriteria.length > 0 && value !== null) {
            criteria = this.state.addedCriteria;
            const singleCriteria = criteria.find(crit => {
                if (crit.property === filter.property) {
                    return crit;
                }
                return null;
            });
            if (singleCriteria) {
                singleCriteria.value = value;
                singleCriteria.filterType = filterType;
                const index = criteria.indexOf(singleCriteria);
                criteria[index] = singleCriteria;
            }
        }
        this.setState({
            pendingAppliedFilters: {
                ...this.state.pendingAppliedFilters,
                [filter.property]: {
                    label: filter.label,
                    type: filter.type,
                    value,
                    filterType,
                },
            },
            addedCriteria: criteria,
        });
    };

    /**
     *
     * @param {string} property
     */
    removeFilter = property => {
        this.setState(prev => {
            const pending = { ...prev.pendingAppliedFilters };
            delete pending[property];

            const applied = { ...prev.appliedFilters };
            delete applied[property];

            return {
                pendingAppliedFilters: pending,
                appliedFilters: applied,
            };
        });
    };

    applyFilters = () => {
        this.setState(
            prev => {
                const kept = Object.fromEntries(
                    Object.entries(prev.appliedFilters).filter(([key]) =>
                        prev.addedCriteria.some(c => c.property === key),
                    ),
                );

                const newApplied = { ...kept, ...prev.pendingAppliedFilters };

                return {
                    appliedFilters: newApplied,
                    pendingAppliedFilters: {},
                    lastAppliedFilterCombinerType: prev.filterCombinerType,
                };
            },
            () => {
                const { filterCombinerType, appliedFilters, addedCriteria } = this.state;
                this.emit('APPLY_POINTS_FILTERS_REQUEST', {
                    filterCombinerType,
                    appliedFilters,
                    addedCriteria,
                });
                this.emit('UPDATE_APPLIED_FILTERS_REQUEST', {
                    appliedGeoFilters: { appliedFilters },
                });
                this.emit('MAP_APPLY_GEO_JSON_LAYER_UPDATE', {
                    filterCombinerType,
                    appliedFilters,
                });
            },
        );
    };

    clearFilters = () => {
        this.setState(
            {
                filters: [],
                appliedFilters: {},
                addedCriteria: [],
                pendingAppliedFilters: {},
                filterCombinerType: FilterCombiner.MATCH_ALL.type,
                lastAppliedFilterCombinerType: FilterCombiner.MATCH_ALL.type,
            },
            () => {
                this.emit('POINTS_FILTERS_REQUEST');
            },
        );

        this.emit('HIDE_FILTER_AND_MASK_INDICATOR');
        this.emit('APPLY_POINTS_FILTERS_REQUEST', {
            appliedFilters: {},
            addedCriteria: [],
            filterCombinerType: FilterCombiner.MATCH_ALL.type,
        });
    };

    /** @param {string} filterCombinerType */
    onCombinerChange = filterCombinerType => {
        this.setState({ filterCombinerType });
    };

    /** @param {object} filter */
    addCriterion = filter => {
        // Make a new property value for the filter. Use current value and append Datetime to it
        const filterProperty = `${filter.property}@${Date.now()}`;
        // add to applied filters
        this.addFilter({ ...filter, property: filterProperty }, filter.value, filter.filterType);
        this.setState({
            addedCriteria: [...this.state.addedCriteria, { ...filter, property: filterProperty }],
        });
    };

    /** @param {object} filter */
    removeCriterion = filter => {
        const addedCriteria = [...this.state.addedCriteria];
        const indexOfCriteriaWithProperty = addedCriteria.findIndex(
            f => f.property === filter.property,
        );
        if (indexOfCriteriaWithProperty !== -1) {
            addedCriteria.splice(indexOfCriteriaWithProperty, 1);
        }
        // Remove from pending filters first
        const pendingAppliedFilters = { ...this.state.pendingAppliedFilters };
        delete pendingAppliedFilters[filter.property];

        this.setState({
            addedCriteria,
            pendingAppliedFilters,
        });
        if (addedCriteria.length === 0) {
            this.emit('HIDE_FILTER_AND_MASK_INDICATOR');
        }
    };

    isApplyButtonEnabled = () => {
        const { 
            appliedFilters, 
            addedCriteria, 
            pendingAppliedFilters,
            filterCombinerType, 
            lastAppliedFilterCombinerType 
        } = this.state;
             
        const appliedFiltersList = Object.entries(appliedFilters || {});
        const pendingFiltersList = Object.entries(pendingAppliedFilters || {});
        const combinerTypeChanged = filterCombinerType !== lastAppliedFilterCombinerType;
        
        // Check if any filter has invalid value
        const hasInvalidFilter = addedCriteria.some(
            filter =>
                filter.value === undefined ||
                filter.value === null ||
                filter.value === '',
        );       
        if (hasInvalidFilter) return false;

        // Check if there are pending changes
        if (pendingFiltersList.length > 0) {
            return true;
        }

        // Check if combiner type changed with existing filters
        if (combinerTypeChanged && (appliedFiltersList.length > 0 || pendingFiltersList.length > 0)) {
            return true;
        }

        // Check if addedCriteria and appliedFilters have different items or values
        const filtersAreDifferent = addedCriteria.some(criterion => {
            const appliedFilter = appliedFilters[criterion.property];
            // If no corresponding applied filter exists
            if (!appliedFilter) return true;
            
            // If the filter exists but has different value
            if (appliedFilter.value !== criterion.value) return true;
            
            return false;
        });

        // Also check if there are filters in appliedFilters that are not in addedCriteria
        const appliedPropertiesNotInCriteria = Object.keys(appliedFilters).some(
            property => !addedCriteria.some(criterion => criterion.property === property)
        );

        return filtersAreDifferent || appliedPropertiesNotInCriteria;
    };

    render() {
        const { intl, hidden } = this.props;
        const {
            addedCriteria,
            appliedFilters,
            filterCombinerType,
        } = this.state;

        const isApplyButtonDisabled = !this.isApplyButtonEnabled();

        const filters = addedCriteria.map(filter => (
            <div key={filter.property} className="flex-it center filter-wrapper">
                <Filter
                    filter={filter}
                    addFilter={this.addFilter}
                    removeFilter={this.removeFilter}
                />
                <div className="flex-it center">
                    <div className="divider divider--vertical divider--btn-separator" />
                    <button
                        className="btn-icon btn-icon--small"
                        onClick={() => this.removeCriterion(filter)}
                    >
                        <i className="material-icons">close</i>
                    </button>
                </div>
            </div>
        ));

        return (
            <div
                className={classNames('points-filter-panel', {
                    hidden: hidden,
                })}
            >
                <div className="flex-it center points-filter-panel__header">
                    <FormattedMessage
                        id={`${AppConfig.constants.searchBox.findByCriteriaDescription}`}
                        values={{
                            chunk: (
                                <TypeDropdown
                                    value={filterCombinerType}
                                    onChange={this.onCombinerChange}
                                    types={Object.values(FilterCombiner).map(type => ({
                                        ...type,
                                        text: intl.formatMessage({ id: type.text }),
                                    }))}
                                    dropdownClassName="simple-dropdown--primary-rounded"
                                />
                            ),
                        }}
                    />
                </div>
                <div className="points-filter-panel__filters">{filters}</div>
                <div className="flex-it center points-filter-panel__criterion">
                    <NewCriterion addCriterion={this.addCriterion} addedCriteria={addedCriteria} />
                </div>
                <div className="flex-it center flex-end points-filter-panel__footer">
                    {Object.keys(appliedFilters).length > 0 && (
                        <button
                            className="points-filter-panel__clear-filters"
                            onClick={this.clearFilters}
                        >
                            {intl.formatMessage({ id: 'dataBrowser.clearFilters' })}
                        </button>
                    )}
                    <button
                        className="btn-raised"
                        disabled={isApplyButtonDisabled}
                        onClick={this.applyFilters}
                    >
                        {intl.formatMessage({ id: `${AppConfig.constants.filterButtonText}` })}
                    </button>
                </div>
            </div>
        );
    }
}

export default injectIntl(FilterPanel);
